/**
 * Shared help-content building blocks, used by BOTH the per-page slide-over
 * (HelpPanel) and the dedicated /help guide — so every page's help is authored
 * once in the registry and rendered identically in both places.
 *
 * Section / Code / K were originally local to Help.tsx; they live here now and
 * Help.tsx re-imports them.
 */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { Badge } from "../components/Badge";

/** Which nav grouping a page belongs to (drives the /help guide ordering). */
export type NavGroup = "main" | "chassis" | "tools" | "settings";

export type HelpItem = { label: string; body: ReactNode };
export type HelpShot = { src: string; caption: string };
export type HelpRelated = { label: string; to: string };

export type HelpEntry = {
  /** Stable id; also the anchor on /help and the topic passed to HelpButton. */
  id: string;
  title: string;
  /** Human route hint shown in the header, e.g. "/settings/vpn". */
  route: string;
  navGroup: NavGroup;
  adminOnly?: boolean;
  /** One or two sentences: what the page is for. */
  summary: ReactNode;
  /** "What each item does" — one row per notable control/section. */
  items: HelpItem[];
  shots?: HelpShot[];
  related?: HelpRelated[];
};

export function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-20 flex flex-col gap-2">
      <h2 className="border-b border-slate-800 pb-1 text-base font-semibold text-slate-100">
        {title}
      </h2>
      <div className="flex flex-col gap-2 text-sm text-slate-300">{children}</div>
    </section>
  );
}

export function Code({ children }: { children: ReactNode }) {
  return (
    <pre className="overflow-x-auto rounded border border-slate-800 bg-slate-950 p-3 font-mono text-[11px] leading-relaxed text-slate-200">
      {children}
    </pre>
  );
}

export function K({ children }: { children: ReactNode }) {
  return (
    <code className="rounded bg-slate-800/70 px-1 py-0.5 font-mono text-[11px] text-emerald-200">
      {children}
    </code>
  );
}

/**
 * A captioned screenshot. Images live in apps/frontend/public/help/ and are
 * served same-origin (CSP-safe). If an image hasn't been captured yet, the
 * <img> onError swaps in a muted placeholder so the help never shows a broken
 * image — the shot can be dropped in later with no code change.
 */
export function Shot({ src, caption }: HelpShot) {
  return (
    <figure className="flex flex-col gap-1">
      <div className="overflow-hidden rounded-lg border border-slate-800 bg-slate-950">
        <img
          src={src}
          alt={caption}
          loading="lazy"
          className="block w-full outline outline-1 -outline-offset-1 outline-black/10 dark:outline-white/10"
          onError={(e) => {
            const img = e.currentTarget;
            // Avoid an infinite loop if the placeholder itself 404s.
            if (img.dataset.fallback === "1") return;
            img.dataset.fallback = "1";
            img.style.display = "none";
            const ph = img.nextElementSibling as HTMLElement | null;
            if (ph) ph.style.display = "flex";
          }}
        />
        <div
          style={{ display: "none" }}
          className="h-40 items-center justify-center px-4 text-center text-[11px] text-slate-500"
        >
          Screenshot pending — sample view of this page will appear here.
        </div>
      </div>
      <figcaption className="text-[11px] text-slate-500">{caption}</figcaption>
    </figure>
  );
}

/** Renders one registry entry — the unit shared by the panel and the guide. */
export function HelpTopic({ entry }: { entry: HelpEntry }) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-slate-100">{entry.title}</h3>
        {entry.adminOnly && <Badge tone="warning">Admin only</Badge>}
        <code className="rounded bg-slate-800/70 px-1 py-0.5 font-mono text-[10px] text-slate-400">
          {entry.route}
        </code>
      </div>

      <div className="text-sm text-slate-300">{entry.summary}</div>

      {entry.items.length > 0 && (
        <dl className="flex flex-col gap-1.5">
          {entry.items.map((it) => (
            <div
              key={it.label}
              className="grid grid-cols-[10rem_1fr] gap-x-3 gap-y-0.5 max-sm:grid-cols-1"
            >
              <dt className="font-mono text-[11px] text-emerald-200">
                {it.label}
              </dt>
              <dd className="text-[13px] text-slate-300">{it.body}</dd>
            </div>
          ))}
        </dl>
      )}

      {entry.shots && entry.shots.length > 0 && (
        <div className="flex flex-col gap-3">
          {entry.shots.map((s) => (
            <Shot key={s.src} {...s} />
          ))}
        </div>
      )}

      {entry.related && entry.related.length > 0 && (
        <p className="text-[12px] text-slate-400">
          See also:{" "}
          {entry.related.map((r, i) => (
            <span key={r.to}>
              {i > 0 && ", "}
              <Link to={r.to} className="text-emerald-400 hover:text-emerald-300">
                {r.label}
              </Link>
            </span>
          ))}
        </p>
      )}
    </div>
  );
}
